import StreamItem from '@anm/components/stream/StreamItem';
import getWebinarPreview from '@anm/helpers/getWebinarPreview';
import useDownloadStreamVideo from '@anm/hooks/useDownloadStreamVideo';
import useHover from '@anm/hooks/useHover';
import useSwitchState from '@anm/hooks/useSwitchState';
import { memo, FC } from 'react';
import { useSelector } from 'react-redux';
import { LiveStream } from 'types/stream';

import useFetchGuestsDestinations from '../../../hooks/useFetchGuestDestinations';
import routes from '../../../routes';
import { userSelectors } from '../../../store/modules/user';
import { getRoomUrl, getStreamStatus } from '../../my-projects/MyStreamsSection/helpers';
import useDeleteRecordingModal from '../../my-projects/MyStreamsSection/hooks/useDeleteRecordingModal';
import useDeleteStream from '../../my-projects/MyStreamsSection/hooks/useDeleteStream';
import useStreamDestinations from '../../my-projects/MyStreamsSection/hooks/useStreamDestinations';
import useProjectsPermissions from '../../my-projects/hooks/useProjectsPermissions';
import EditStreamModal from '../StreamModalWrapper';

import {
  createProjectFromRecordings,
  getDuration,
  getProjectLink,
  getStreamDates,
  openRecordingFolder
} from './helpers';
import {
  useCloseModalAfterSettingsUpdated,
  useDuplicateStream,
  useEditDestinations,
  useEditStreamTitle,
  useStopStream
} from './hooks';
import useMoveStreamToWorkspaceAction from './useMoveStreamToWorkspaceAction';

type StreamItemWrapperProps = LiveStream & {
  showPreview?: boolean;
  isFirstCard?: boolean;
};

const StreamItemWrapper: FC<StreamItemWrapperProps> = props => {
  const { meta, kind, state, added, error, result, streamId, schedule, destinations, ...rest } = props;
  const { title, description, preview: streamPreview, roomId, projectId } = meta;
  const { uploadId, startedAt, hostedVideoId, webinarId, width, height, recordingFolderId } = meta;

  const isVertical = width && height ? width / height < 1 : false;

  const preview = webinarId ? getWebinarPreview({ isVertical, previewUrl: streamPreview }) : streamPreview;

  const moveWorkspaceAction = useMoveStreamToWorkspaceAction({ streamId });
  const hasTeamAccess = useSelector(userSelectors.selectTeamAccess);
  const { canEditProject } = useProjectsPermissions();

  const roomUrl = getRoomUrl({ id: roomId, kind, state });
  const isStaff = useSelector(userSelectors.selectIsStaffUser);
  const maxGuests = useSelector(userSelectors.selectStreamMaxGuests);

  const status = getStreamStatus(state, kind, schedule);
  const projectLink = getProjectLink(hostedVideoId || projectId);

  const [isDeletePending, handleDeleteStream, uploadProjects, fetchUploadProjects] = useDeleteStream(
    kind,
    projectId,
    uploadId,
    !!webinarId
  );
  const streamDestinations = useStreamDestinations(destinations, streamId);

  const [itemDate, createDate] = getStreamDates({ added, schedule, kind, state, startedAt });
  const [isOpenEditStreamModal, openEditStreamModal, closeEditStreamModal] = useSwitchState();

  const handleDownloadVideo = useDownloadStreamVideo(meta.uploadId);

  const [ref, isHovered] = useHover<HTMLDivElement>(300);

  const userProfile = useSelector(userSelectors.selectUserProfile);
  const hasGuestDestinations = !!streamDestinations?.some(d => d.user_id !== userProfile?.userId);
  const canFetchGuests = hasGuestDestinations && isHovered;

  useFetchGuestsDestinations(canFetchGuests, streamId);

  const [isEditDestinations, handelEditDestinations] = useEditDestinations(openEditStreamModal);

  const handleStopStream = useStopStream(streamId);
  const [isDuplicate, projectToStream, handleDuplicate, handleStreamVideo] = useDuplicateStream({
    kind,
    streamId,
    projectId,
    closeEditStreamModal,
    openEditStreamModal
  });

  const handleModalClose = () => {
    isDuplicate.current = false;
    isEditDestinations.current = false;
    projectToStream.current = undefined;
    closeEditStreamModal();
  };

  const handleCreateProjectFromRecordings = recordingFolderId ? () => createProjectFromRecordings(streamId) : undefined;

  const handelOpenRecordingFolder = recordingFolderId ? () => openRecordingFolder(recordingFolderId) : undefined;

  const handleEditStreamTitle = useEditStreamTitle(streamId);
  const handelDeleteRecording = useDeleteRecordingModal(streamId);
  useCloseModalAfterSettingsUpdated({ roomId, isOpenModal: isOpenEditStreamModal, closeModal: handleModalClose });

  const isUserFree = useSelector(userSelectors.selectIsUserFree);
  const uploadProjectsExceptCurrent = uploadProjects?.filter(({ id }) => id !== projectId);

  const isWebinar = !!webinarId;
  const hasFile = isWebinar ? !!uploadId && state !== 'SCHEDULED' : !!uploadId;
  const isMaxSubscription = useSelector(userSelectors.selectIsMaxWavePlan);

  return (
    <div ref={ref}>
      {isOpenEditStreamModal && (
        <EditStreamModal
          isEdit
          videoSrc={result?.url || rest.input}
          isDuplicate={isDuplicate.current}
          projectId={projectToStream.current}
          isEditDestinations={isEditDestinations.current}
          {...{ streamId, roomId, streamDestinations, webinarId }}
          isHosted={kind === 'HOSTED' || !!projectToStream.current}
          onClose={handleModalClose}
        />
      )}
      <StreamItem
        src={preview}
        date={itemDate}
        canDebug={isStaff}
        studioLink={roomUrl}
        hasFile={hasFile}
        streamError={error?.message}
        createDate={createDate}
        onDelete={handleDeleteStream}
        duration={getDuration(meta, state)}
        pausedDuration={meta?.paused?.stats.duration}
        additionalActions={state === 'SCHEDULED' && hasTeamAccess && canEditProject ? [moveWorkspaceAction] : undefined}
        onDuplicate={handleDuplicate}
        onStopStream={handleStopStream}
        onStreamVideo={handleStreamVideo}
        onOpenEditModal={openEditStreamModal}
        onDownloadVideo={handleDownloadVideo}
        onTitleUpdate={handleEditStreamTitle}
        destinations={streamDestinations}
        onEditDestinations={handelEditDestinations}
        onDeleteRecording={handelDeleteRecording}
        onOpenRecordingFolder={handelOpenRecordingFolder}
        uploadProjects={uploadProjectsExceptCurrent}
        onCustomize={() => routes.Router.pushRoute(`webinar/${webinarId}`)}
        onCreateProjectFromRecording={handleCreateProjectFromRecordings}
        startedAt={startedAt ? new Date(startedAt).getTime() : undefined}
        {...{ kind, state, roomId, isDeletePending, projectLink, fetchUploadProjects, isUserFree }}
        {...{ maxGuests, streamId, title, description, status, schedule, webinarId, meta, isMaxSubscription, ...rest }}
      />
    </div>
  );
};

export default memo(StreamItemWrapper);
